<script lang="ts">
  import { api, type SearchResult } from "../lib/api";
  import { haptic } from "../lib/tg";

  let {
    open = $bindable(false),
  }: {
    open?: boolean;
  } = $props();

  let query = $state("");
  let results = $state<SearchResult | null>(null);
  let loading = $state(false);
  let input: HTMLInputElement | null = $state(null);
  let timer: ReturnType<typeof setTimeout> | null = null;

  $effect(() => {
    if (open && input) {
      setTimeout(() => input?.focus(), 200);
    }
  });

  function close() {
    open = false;
    query = "";
    results = null;
    if (timer) clearTimeout(timer);
  }

  function onInput() {
    if (timer) clearTimeout(timer);
    const q = query.trim();
    if (q.length < 2) {
      results = null;
      return;
    }
    timer = setTimeout(async () => {
      loading = true;
      try {
        results = await api.search(q);
      } catch (e) {
        results = null;
      } finally {
        loading = false;
      }
    }, 250);
  }

  function go(href: string) {
    haptic("medium");
    close();
    history.pushState(null, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  function moneyShort(rub: number): string {
    if (rub >= 1_000_000) return `${(rub / 1_000_000).toFixed(1).replace(/\.0$/, "")} млн ₽`;
    if (rub >= 1000) return `${Math.floor(rub / 1000)}к ₽`;
    return `${rub} ₽`;
  }
</script>

{#if open}
  <div class="backdrop" onclick={close} role="presentation"></div>
  <div class="sheet" role="dialog" aria-label="Поиск">
    <div class="search-row">
      <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="7"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input
        bind:this={input}
        bind:value={query}
        oninput={onInput}
        type="search"
        autocomplete="off"
        placeholder="Имя клиента, название сделки, текст задачи…"
      />
      <button class="cancel" onclick={close} type="button">Готово</button>
    </div>

    <div class="results">
      {#if loading}
        <div class="hint">Ищу…</div>
      {:else if query.trim().length < 2}
        <div class="hint">Введи 2+ символа</div>
      {:else if !results}
        <div class="hint">—</div>
      {:else if results.leads.length === 0 && results.tasks.length === 0 && results.contacts.length === 0}
        <div class="hint">Ничего не нашлось</div>
      {:else}
        {#if results.leads.length > 0}
          <div class="sec-title">Сделки · {results.leads.length}</div>
          {#each results.leads as l}
            <button class="row" onclick={() => go(`?deal=${l.amo_id}`)} type="button">
              <div class="row-top">
                <div class="row-text">{l.name}</div>
                {#if l.price_rub > 0}<div class="row-price">{moneyShort(l.price_rub)}</div>{/if}
              </div>
              <div class="row-meta">{l.manager_name}</div>
            </button>
          {/each}
        {/if}

        {#if results.tasks.length > 0}
          <div class="sec-title">Задачи · {results.tasks.length}</div>
          {#each results.tasks as t}
            <button class="row"
                    onclick={() => t.lead_amo_id ? go(`?deal=${t.lead_amo_id}`) : null}
                    type="button">
              <div class="row-text">{t.text}</div>
              <div class="row-meta">{t.responsible_name}</div>
            </button>
          {/each}
        {/if}

        {#if results.contacts.length > 0}
          <div class="sec-title">Контакты · {results.contacts.length}</div>
          {#each results.contacts as c}
            <div class="row static">
              <div class="row-text">{c.name}</div>
              <div class="row-meta">
                {c.phone ?? ""}{#if c.phone && c.email} · {/if}{c.email ?? ""}
              </div>
            </div>
          {/each}
        {/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 60;
    animation: fade 160ms ease;
  }
  .sheet {
    position: fixed;
    left: 0; right: 0;
    top: 0; bottom: 0;
    background: var(--bg);
    z-index: 70;
    display: flex; flex-direction: column;
    animation: slide-down 200ms cubic-bezier(.18,.89,.32,1.05);
  }
  .search-row {
    display: flex; align-items: center; gap: 8px;
    padding: calc(10px + var(--safe-top, 0px)) 14px 10px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-elev);
  }
  .search-icon {
    width: 18px; height: 18px;
    color: var(--text-mute);
    flex-shrink: 0;
  }
  input {
    flex: 1;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 10px 12px;
    font-size: 15px;
    color: var(--text);
    font-family: inherit;
  }
  input:focus { outline: none; border-color: var(--ember); }
  .cancel {
    background: transparent;
    border: none;
    color: var(--ember);
    font-weight: 600;
    font-size: 14px;
    padding: 8px 4px;
    cursor: pointer;
  }
  .results {
    flex: 1;
    overflow-y: auto;
    padding: 8px 14px calc(20px + var(--safe-bottom, 0px));
  }
  .hint {
    padding: 30px 16px;
    text-align: center;
    color: var(--text-mute);
    font-size: 13px;
  }
  .sec-title {
    padding: 14px 4px 6px;
    font-family: 'Manrope', sans-serif;
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-dim);
  }
  .row {
    display: block;
    width: 100%;
    text-align: left;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 11px 14px;
    margin-bottom: 6px;
    color: var(--text);
    cursor: pointer;
    transition: transform 80ms ease;
    font-family: inherit;
  }
  .row:active { transform: scale(0.99); }
  .row.static { cursor: default; }
  .row-top {
    display: flex; justify-content: space-between;
    gap: 10px; align-items: center;
  }
  .row-text {
    font-family: 'Manrope', sans-serif;
    font-weight: 600; font-size: 14px;
  }
  .row-price {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px; color: var(--brass); font-weight: 600;
  }
  .row-meta {
    margin-top: 3px;
    font-size: 11px;
    color: var(--text-mute);
  }
  @keyframes fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slide-down { from { transform: translateY(-10%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
</style>
